import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveRuleType } from 'Utils';
import { DateService, NumberService } from 'Services';
import { Levels } from 'Common/enums';
import useGetDeployedTenantGroupOverrides from 'Hooks/query-hooks/tenant-groups/useGetDeployedTenantGroupOverrides';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetTenantGroup from 'Hooks/query-hooks/tenant-groups/useGetTenantGroup';
import useGetTenantGroupRulesColumns from 'Hooks/useGetTenantGroupRulesColumns';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';
import errorController from 'Components/ErrorScreens';
import RouteLoading from 'Components/RouteLoading';
import TenantGroupPageHeader from '../TenantGroup/components/TenantGroupPageHeader';
import TenantGroupRulesTable from '../TenantGroup/components/TenantGroupRulesTable';
import TenantGroupOverview from '../TenantGroup/components/TenantGroupOverview';

import styles from './TenantGroupDeployed.css';

const TenantGroupDeployed = () => {
    const { t } = useTranslation();
    const { tenantGroupId } = useParams<{ tenantGroupId: string }>();
    const { users } = useGetReduxUsers();
    const tenantGroupAPI = useGetTenantGroup({
        tenantGroupId
    });
    const globalRulesAPI = useGlobalRules({});
    const listNamesAPI = useGetListNames();
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const deployedTenantGroupOverridesAPI = useGetDeployedTenantGroupOverrides({
        tenantGroupId,
        users,
        enabled:
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });
    const { hasError, errorCmp } = errorController([
        deployedTenantGroupOverridesAPI.error,
        tenantGroupAPI.error,
        globalRulesAPI.error
    ]);
    const ruleTableColumns = useGetTenantGroupRulesColumns();
    const tenantGroupOverride = React.useMemo(() => {
        return {
            ...deployedTenantGroupOverridesAPI.data,
            rules: deployedTenantGroupOverridesAPI.data?.rules
                .filter((rule) => rule.inherit === Levels.TENANT_GROUP)
                .map((rule) => {
                    return { ...rule, ruleType: resolveRuleType(rule) };
                })
        };
    }, [deployedTenantGroupOverridesAPI.data]);
    const formattedDate =
        DateService.getDateTime(deployedTenantGroupOverridesAPI.data?.createdAt) ??
        deployedTenantGroupOverridesAPI.data?.createdAt;
    const formattedRevision = NumberService.getLocaleNumber(Number(deployedTenantGroupOverridesAPI.data?.revision));
    const deployedInfoText = t('deployedInfoText', {
        revision: formattedRevision,
        createdAt: formattedDate,
        createdBy: deployedTenantGroupOverridesAPI.data?.createdBy
    });

    if (
        deployedTenantGroupOverridesAPI.isLoading ||
        deployedTenantGroupOverridesAPI.isFetching ||
        tenantGroupAPI.isLoading ||
        tenantGroupAPI.isFetching ||
        globalRulesAPI.isLoading ||
        globalRulesAPI.isFetching
    )
        return <RouteLoading />;
    if (hasError) return <>{errorCmp}</>;

    return (
        <div className={styles.pageContainer}>
            <TenantGroupPageHeader
                mode='deployed'
                disableViewDeployed={!deployedTenantGroupOverridesAPI.data?.isDeployed}
                tenantGroupName={tenantGroupAPI.data?.name}
                tenantGroupId={tenantGroupId}
                deployedInfoText={deployedInfoText}
            />

            <TenantGroupOverview
                // @ts-ignore
                ruleData={tenantGroupOverride!}
                tenantGroupId={tenantGroupId}
                tenantOverridesData={deployedTenantGroupOverridesAPI.data}
                mode='deployed'
            />

            <TenantGroupRulesTable
                columnNames={ruleTableColumns}
                globalRules={globalRulesAPI.data!}
                // @ts-ignore
                tenantOverrideRules={tenantGroupOverride}
                mode='deployed'
            />
        </div>
    );
};

export default TenantGroupDeployed;
